(function () {
  const rootSelector = '[data-dc-shipping-page]';
  const formSelector = '[data-dc-shipping-form]';
  const thresholdCents = 100000;
  const searchDelay = 300;
  const customPhoneCodePattern = /^\+[1-9][0-9]{0,4}$/;
  const trackingStorageKey = 'diggerCityShippingQuotePending';
  const pendingContactFormTypeKey = 'diggerCityPendingContactFormType';
  const pendingContactFormIdKey = 'diggerCityPendingContactFormId';
  const trackingExpiryMs = 30 * 60 * 1000;
  const openedRoots = new WeakSet();
  const searchState = new WeakMap();

  function sanitizeText(value, maxLength = 200) {
    return String(value || '')
      .replace(/[\u0000-\u001f\u007f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maxLength);
  }

  function normalizeChoice(value) {
    const text = sanitizeText(value, 80).toLowerCase();
    if (!text) return '';
    if (text.indexOf('urgent') === 0) return 'urgent';
    if (text.indexOf('flexible') === 0) return 'flexible_backload';
    if (text.indexOf('specific') === 0) return 'specific';
    if (text.indexOf('planning') !== -1) return 'planning';
    if (text.indexOf('yes') === 0) return 'yes';
    if (text.indexOf('no') === 0) return 'no';
    if (text.indexOf('sure') !== -1) return 'unsure';
    return sanitizeText(value, 80);
  }

  function escapeHTML(value) {
    return sanitizeText(value, 240)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function cleanPath() {
    return window.location && window.location.pathname ? window.location.pathname : '';
  }

  function cleanUrl(value) {
    try {
      const url = new URL(value || cleanPath(), window.location.origin);
      return `${url.origin}${url.pathname}`;
    } catch (error) {
      return cleanPath();
    }
  }

  function generateEventId() {
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      const values = new Uint32Array(2);
      window.crypto.getRandomValues(values);
      return `sq_${Date.now().toString(36)}_${values[0].toString(36)}${values[1].toString(36)}`;
    }
    return `sq_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
  }

  function getField(form, selector) {
    return form ? form.querySelector(selector) : null;
  }

  function getValue(form, selector) {
    const field = getField(form, selector);
    return field ? field.value : '';
  }

  function getSelectedProduct(root) {
    const state = searchState.get(root);
    return state && state.selectedProduct ? state.selectedProduct : null;
  }

  function getActiveQuoteType(root) {
    const activeTab = root.querySelector('[data-dc-shipping-tab][aria-selected="true"]');
    return activeTab && activeTab.dataset.dcShippingTab === 'international' ? 'international' : 'nz_delivery';
  }

  function getEventId(form) {
    const field = form.querySelector('[data-dc-shipping-event-id]');
    if (!field) return '';
    if (!field.value) field.value = generateEventId();
    return field.value;
  }

  function getBroadDeliverySummary(value) {
    const text = sanitizeText(value, 120);
    if (!text) return '';
    const parts = text.split(',').map((part) => sanitizeText(part, 40)).filter(Boolean);
    return parts.length > 1 ? parts[parts.length - 1] : parts[0];
  }

  function getTrackingPayload(root, form) {
    const product = getSelectedProduct(root) || {};
    const quoteType = getActiveQuoteType(root);
    return {
      event_id: getEventId(form),
      quote_type: quoteType,
      product_title: sanitizeText(product.title, 200),
      product_handle: sanitizeText(product.handle, 120),
      product_id: sanitizeText(product.id, 80),
      variant_id: sanitizeText(product.variantId, 80),
      sku: sanitizeText(product.sku, 120),
      price: Number(product.price || 0) / 100,
      currency: sanitizeText(product.currency || root.dataset.currency || '', 20),
      delivery_address_summary: quoteType === 'nz_delivery' ? getBroadDeliverySummary(getValue(form, '[data-dc-delivery-address]')) : '',
      delivery_region: '',
      depot_ok: getField(form, '[data-dc-depot-checkbox]') && getField(form, '[data-dc-depot-checkbox]').checked ? 'yes' : 'no',
      site_access: normalizeChoice(getValue(form, '[data-dc-site-access]')),
      urgency: normalizeChoice(quoteType === 'international' ? getValue(form, '[data-dc-urgency-international]') : getValue(form, '[data-dc-urgency]')),
      nearest_port: quoteType === 'international' ? sanitizeText(getValue(form, '[data-dc-nearest-port]'), 120) : '',
      country: '',
      page_path: cleanPath(),
      product_url: cleanUrl(product.url || ''),
      form_id: form.id || '',
      form_context: 'shipping_page',
      event_timestamp: Date.now(),
    };
  }

  function sanitizePayload(payload) {
    const safePayload = {};
    Object.keys(payload || {}).forEach((key) => {
      if (key === 'price') {
        const price = Number(payload[key]);
        if (Number.isFinite(price) && price > 0) safePayload[key] = price;
        return;
      }
      if (key === 'event_timestamp') {
        const timestamp = Number(payload[key]);
        safePayload[key] = Number.isFinite(timestamp) && timestamp > 0 ? timestamp : Date.now();
        return;
      }
      if (key === 'page_path') {
        safePayload[key] = cleanPath();
        return;
      }
      if (key === 'product_url') {
        safePayload[key] = cleanUrl(payload[key]);
        return;
      }
      const text = sanitizeText(payload[key], key === 'product_title' ? 200 : 120);
      if (text) safePayload[key] = text;
    });
    return safePayload;
  }

  function publishEvent(eventName, payload) {
    const safePayload = sanitizePayload(payload);
    try {
      window.dispatchEvent(new CustomEvent(eventName, { detail: safePayload }));
    } catch (error) {
      return false;
    }
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      try {
        window.Shopify.analytics.publish(eventName, safePayload);
      } catch (error) {
        return false;
      }
    }
    return true;
  }

  function storePendingPayload(payload) {
    try {
      window.sessionStorage.setItem(trackingStorageKey, JSON.stringify(sanitizePayload(payload)));
    } catch (error) {
      return;
    }
  }

  function getPendingPayload() {
    try {
      const stored = window.sessionStorage.getItem(trackingStorageKey);
      if (!stored) return null;
      const payload = sanitizePayload(JSON.parse(stored));
      const timestamp = Number(payload.event_timestamp);
      if (!timestamp || Date.now() - timestamp > trackingExpiryMs) {
        window.sessionStorage.removeItem(trackingStorageKey);
        return null;
      }
      return payload;
    } catch (error) {
      return null;
    }
  }

  function clearPendingPayload() {
    try {
      window.sessionStorage.removeItem(trackingStorageKey);
    } catch (error) {
      return;
    }
  }

  function storePendingContactForm(formId) {
    try {
      window.sessionStorage.setItem(pendingContactFormTypeKey, 'shipping_quote');
      window.sessionStorage.setItem(pendingContactFormIdKey, formId || '');
    } catch (error) {
      return;
    }
  }

  function getPendingContactForm() {
    try {
      return {
        type: window.sessionStorage.getItem(pendingContactFormTypeKey) || '',
        id: window.sessionStorage.getItem(pendingContactFormIdKey) || '',
      };
    } catch (error) {
      return { type: '', id: '' };
    }
  }

  function clearPendingContactForm() {
    try {
      window.sessionStorage.removeItem(pendingContactFormTypeKey);
      window.sessionStorage.removeItem(pendingContactFormIdKey);
    } catch (error) {
      return;
    }
  }

  function publishOpened(root) {
    if (openedRoots.has(root)) return;
    const form = root.querySelector(formSelector);
    if (!form) return;
    openedRoots.add(root);
    publishEvent('digger_city:shipping_quote_opened', getTrackingPayload(root, form));
  }

  function formatMoney(cents, currency) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, (window.theme && window.theme.moneyFormat) || '${{amount}}');
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'NZD',
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  function getProductImage(product) {
    if (product.featured_image) return product.featured_image;
    if (Array.isArray(product.images) && product.images[0]) return product.images[0];
    return '';
  }

  function normalizeProduct(product, root) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const validVariant = variants.find((variant) => Number(variant.price) >= thresholdCents) || null;
    if (!validVariant) return null;
    const handle = product.handle || '';
    return {
      id: product.id || '',
      handle,
      title: product.title || '',
      url: product.url || (handle ? `/products/${handle}` : ''),
      image: getProductImage(product),
      variantId: validVariant.id || '',
      sku: validVariant.sku || '',
      price: Number(validVariant.price),
      priceText: formatMoney(Number(validVariant.price), root.dataset.currency),
      currency: root.dataset.currency || '',
    };
  }

  function renderStatus(resultsEl, message) {
    resultsEl.hidden = false;
    const input = resultsEl.closest('[data-dc-product-search]')?.querySelector('[data-dc-product-search-input]');
    if (input) input.setAttribute('aria-expanded', 'true');
    resultsEl.innerHTML = `<p class="dc-product-search__status">${message}</p>`;
  }

  function renderProductImage(product) {
    if (product.image) {
      return `<img class="dc-product-search__image" src="${escapeHTML(product.image)}" alt="" loading="lazy">`;
    }
    return '<span class="dc-product-search__image" aria-hidden="true"></span>';
  }

  function renderResults(root, products) {
    const resultsEl = root.querySelector('[data-dc-product-search-results]');
    if (!resultsEl) return;
    if (!products.length) {
      renderStatus(resultsEl, 'No machinery over $1,000 matched that search.');
      return;
    }
    resultsEl.hidden = false;
    const input = root.querySelector('[data-dc-product-search-input]');
    if (input) input.setAttribute('aria-expanded', 'true');
    resultsEl.innerHTML = products.map((product, index) => `
      <button class="dc-product-search__result" type="button" data-dc-product-result="${index}">
        ${renderProductImage(product)}
        <span class="dc-product-search__meta">
          <span class="dc-product-search__title">${escapeHTML(product.title)}</span>
          <span class="dc-product-search__detail">${product.sku ? `SKU: ${escapeHTML(product.sku)}` : 'SKU unavailable'}</span>
          <span class="dc-product-search__detail">${escapeHTML(product.priceText)}</span>
        </span>
      </button>
    `).join('');
  }

  function setProductFields(form, product) {
    const fields = {
      title: product ? product.title : '',
      handle: product ? product.handle : '',
      url: product ? cleanUrl(product.url) : '',
      id: product ? product.id : '',
      variant_id: product ? product.variantId : '',
      sku: product ? product.sku : '',
      price: product ? product.priceText : '',
      currency: product ? product.currency : '',
    };
    Object.keys(fields).forEach((key) => {
      const field = form.querySelector(`[data-dc-product-field="${key}"]`);
      if (field) field.value = fields[key];
    });
    const selectedMachine = form.querySelector('[data-dc-selected-machine]');
    if (selectedMachine) {
      selectedMachine.value = product ? `${product.title}${product.sku ? ` - ${product.sku}` : ''} - ${product.priceText} - ${cleanUrl(product.url)}` : '';
    }
  }

  function renderSelectedProduct(root, product) {
    const selectedEl = root.querySelector('[data-dc-product-selected]');
    if (!selectedEl) return;
    if (!product) {
      selectedEl.hidden = true;
      selectedEl.innerHTML = '';
      return;
    }
    selectedEl.hidden = false;
    selectedEl.innerHTML = `
      <div class="dc-product-search__selected-card">
        ${renderProductImage(product)}
        <span class="dc-product-search__meta">
          <span class="dc-product-search__title">${escapeHTML(product.title)}</span>
          <span class="dc-product-search__detail">${product.sku ? `SKU: ${escapeHTML(product.sku)}` : 'SKU unavailable'}</span>
          <span class="dc-product-search__detail">${escapeHTML(product.priceText)}</span>
        </span>
        <button class="dc-product-search__clear" type="button" data-dc-product-clear aria-label="Remove selected product">&times;</button>
      </div>
    `;
  }

  function setSelectedProduct(root, product) {
    const form = root.querySelector(formSelector);
    const productSearch = root.querySelector('[data-dc-product-search]');
    const state = searchState.get(root) || {};
    state.selectedProduct = product || null;
    searchState.set(root, state);
    if (productSearch) productSearch.classList.toggle('is-selected', Boolean(product));
    if (form) setProductFields(form, product);
    renderSelectedProduct(root, product);
    const error = root.querySelector('[data-dc-product-error]');
    if (error) error.textContent = '';
  }

  function fetchProductByHandle(handle, root) {
    return window.fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => (product ? normalizeProduct(product, root) : null))
      .catch(() => null);
  }

  function searchProducts(root, query) {
    const resultsEl = root.querySelector('[data-dc-product-search-results]');
    if (!resultsEl) return;
    renderStatus(resultsEl, 'Searching...');
    const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;
    window.fetch(url, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const rawProducts = data && data.resources && data.resources.results && Array.isArray(data.resources.results.products)
          ? data.resources.results.products
          : [];
        const handles = rawProducts.map((product) => product.handle).filter(Boolean).slice(0, 8);
        return Promise.all(handles.map((handle) => fetchProductByHandle(handle, root)));
      })
      .then((products) => {
        const validProducts = products.filter(Boolean);
        const state = searchState.get(root) || {};
        state.results = validProducts;
        searchState.set(root, state);
        renderResults(root, validProducts);
      })
      .catch(() => {
        renderStatus(resultsEl, 'Product search is unavailable. Please try again.');
      });
  }

  function queueSearch(root, query) {
    const resultsEl = root.querySelector('[data-dc-product-search-results]');
    const state = searchState.get(root) || {};
    const normalizedQuery = sanitizeText(query, 80);
    window.clearTimeout(state.timer);
    if (!normalizedQuery) {
      if (resultsEl) {
        resultsEl.hidden = true;
        resultsEl.innerHTML = '';
        const input = root.querySelector('[data-dc-product-search-input]');
        if (input) input.setAttribute('aria-expanded', 'false');
      }
      state.results = [];
      searchState.set(root, state);
      return;
    }
    state.timer = window.setTimeout(() => searchProducts(root, normalizedQuery), searchDelay);
    searchState.set(root, state);
  }

  function syncConditionalDate(root, type) {
    const select = root.querySelector(type === 'international' ? '[data-dc-urgency-international]' : '[data-dc-urgency]');
    const wrap = root.querySelector(`[data-dc-date-wrap="${type}"]`);
    const input = root.querySelector(`[data-dc-date-input="${type}"]`);
    if (!select || !wrap || !input) return;
    const shouldShow = select.value === 'Urgent' || select.value === 'Specific';
    wrap.hidden = !shouldShow;
    input.disabled = !shouldShow;
    input.required = shouldShow;
    if (!shouldShow) input.value = '';
  }

  function syncPhoneCode(root, quoteType) {
    const group = root.querySelector('.dc-shipping-form__phone-group');
    const wrap = root.querySelector('[data-dc-phone-code-wrap]');
    const select = root.querySelector('[data-dc-phone-code]');
    const customWrap = root.querySelector('[data-dc-phone-code-custom-wrap]');
    const customInput = root.querySelector('[data-dc-phone-code-custom]');
    const isInternational = quoteType === 'international';
    if (wrap) wrap.hidden = !isInternational;
    if (select) {
      select.disabled = !isInternational;
      if (!isInternational) select.value = '';
    }
    const useCustomCode = isInternational && select && select.value === 'Other';
    if (group) {
      group.classList.toggle('has-phone-code', isInternational);
      group.classList.toggle('has-custom-phone-code', useCustomCode);
    }
    if (customWrap) customWrap.hidden = !useCustomCode;
    if (customInput) {
      customInput.disabled = !useCustomCode;
      customInput.required = useCustomCode;
      if (useCustomCode) {
        customInput.setAttribute('pattern', customPhoneCodePattern.source);
      } else {
        customInput.value = '';
        customInput.removeAttribute('pattern');
      }
    }
  }

  function switchTab(root, quoteType) {
    const safeType = quoteType === 'international' ? 'international' : 'nz_delivery';
    root.querySelectorAll('[data-dc-shipping-tab]').forEach((tab) => {
      const active = tab.dataset.dcShippingTab === safeType;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });
    root.querySelectorAll('[data-dc-shipping-panel]').forEach((panel) => {
      const active = panel.dataset.dcShippingPanel === safeType;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
      panel.querySelectorAll('input, select, textarea').forEach((field) => {
        const shouldRequire = active && field.hasAttribute('data-was-required');
        if (field.hasAttribute('required')) field.setAttribute('data-was-required', 'true');
        field.required = shouldRequire || (active && field.hasAttribute('data-was-required'));
        field.disabled = !active;
      });
    });
    const form = root.querySelector(formSelector);
    const quoteInput = root.querySelector('[data-dc-shipping-quote-type-input]');
    const subject = root.querySelector('[data-dc-shipping-subject]');
    syncPhoneCode(root, safeType);
    syncConditionalDate(root, 'nz');
    syncConditionalDate(root, 'international');
    if (quoteInput) quoteInput.value = safeType === 'international' ? 'International shipping' : 'NZ delivery';
    if (subject) subject.value = safeType === 'international' ? 'Shipping Quote Request - International Shipping' : 'Shipping Quote Request - NZ Delivery';
    if (form) getEventId(form);
  }

  function syncDepot(root) {
    const hidden = root.querySelector('[data-dc-depot-hidden]');
    const checkbox = root.querySelector('[data-dc-depot-checkbox]');
    if (hidden) hidden.value = checkbox && checkbox.checked ? 'Yes' : 'No';
  }

  function validateProduct(root) {
    const product = getSelectedProduct(root);
    const error = root.querySelector('[data-dc-product-error]');
    if (!product || !Number.isFinite(Number(product.price)) || Number(product.price) < thresholdCents) {
      if (error) error.textContent = 'Please select a machine from the product search results.';
      return false;
    }
    if (error) error.textContent = '';
    return true;
  }

  function initRoot(root) {
    const form = root.querySelector(formSelector);
    if (!form) return;
    searchState.set(root, { results: [], selectedProduct: null, timer: null });
    getEventId(form);
    switchTab(root, 'nz_delivery');
    syncDepot(root);
    syncConditionalDate(root, 'nz');
    syncConditionalDate(root, 'international');

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          publishOpened(root);
          observer.disconnect();
        }
      }, { threshold: 0.35 });
      observer.observe(form);
    }

    if (root.dataset.success === 'true' || root.querySelector('[data-dc-shipping-success]')) {
      const pendingContactForm = getPendingContactForm();
      const pendingPayload = getPendingPayload();
      if (pendingPayload && pendingContactForm.type === 'shipping_quote' && (!pendingContactForm.id || pendingContactForm.id === root.dataset.successFormId)) {
        publishEvent('digger_city:shipping_quote_submitted', pendingPayload);
        clearPendingPayload();
        clearPendingContactForm();
      }
      const success = root.querySelector('[data-dc-shipping-success]');
      if (success) success.focus();
    }
  }

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-dc-shipping-tab]');
    if (tab) {
      const root = tab.closest(rootSelector);
      if (root) switchTab(root, tab.dataset.dcShippingTab);
      return;
    }

    const result = event.target.closest('[data-dc-product-result]');
    if (result) {
      const root = result.closest(rootSelector);
      const state = root ? searchState.get(root) : null;
      const product = state && state.results ? state.results[Number(result.dataset.dcProductResult)] : null;
      if (root && product) {
        setSelectedProduct(root, product);
        const resultsEl = root.querySelector('[data-dc-product-search-results]');
        if (resultsEl) {
          resultsEl.hidden = true;
          const input = root.querySelector('[data-dc-product-search-input]');
          if (input) input.setAttribute('aria-expanded', 'false');
        }
      }
      return;
    }

    const clear = event.target.closest('[data-dc-product-clear]');
    if (clear) {
      const root = clear.closest(rootSelector);
      if (root) {
        setSelectedProduct(root, null);
        const input = root.querySelector('[data-dc-product-search-input]');
        if (input) {
          input.value = '';
          input.setAttribute('aria-expanded', 'false');
        }
        const resultsEl = root.querySelector('[data-dc-product-search-results]');
        if (resultsEl) {
          resultsEl.hidden = true;
          resultsEl.innerHTML = '';
        }
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    const tab = event.target.closest('[data-dc-shipping-tab]');
    if (!tab || (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft')) return;
    const root = tab.closest(rootSelector);
    if (!root) return;
    const tabs = Array.from(root.querySelectorAll('[data-dc-shipping-tab]'));
    const index = tabs.indexOf(tab);
    const offset = event.key === 'ArrowRight' ? 1 : -1;
    const nextTab = tabs[(index + offset + tabs.length) % tabs.length];
    event.preventDefault();
    nextTab.focus();
    switchTab(root, nextTab.dataset.dcShippingTab);
  });

  document.addEventListener('input', (event) => {
    const searchInput = event.target.closest('[data-dc-product-search-input]');
    if (searchInput) {
      const root = searchInput.closest(rootSelector);
      if (root) {
        setSelectedProduct(root, null);
        queueSearch(root, sanitizeText(searchInput.value, 80));
      }
      return;
    }

    const root = event.target.closest(rootSelector);
    if (root) publishOpened(root);
  });

  document.addEventListener('change', (event) => {
    const root = event.target.closest(rootSelector);
    if (!root) return;
    publishOpened(root);
    if (event.target.matches('[data-dc-depot-checkbox]')) syncDepot(root);
    if (event.target.matches('[data-dc-phone-code]')) syncPhoneCode(root, getActiveQuoteType(root));
    if (event.target.matches('[data-dc-urgency]')) syncConditionalDate(root, 'nz');
    if (event.target.matches('[data-dc-urgency-international]')) syncConditionalDate(root, 'international');
  });

  document.addEventListener('focusin', (event) => {
    const root = event.target.closest(rootSelector);
    if (root && event.target.closest(formSelector)) publishOpened(root);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest(formSelector);
    if (!form) return;
    const root = form.closest(rootSelector);
    if (!root) return;
    syncDepot(root);
    publishOpened(root);
    if (!validateProduct(root)) {
      event.preventDefault();
      const error = root.querySelector('[data-dc-product-error]');
      if (error) error.focus && error.focus();
      return;
    }
    if (!form.checkValidity()) {
      event.preventDefault();
      if (typeof form.reportValidity === 'function') form.reportValidity();
      return;
    }
    storePendingPayload(getTrackingPayload(root, form));
    storePendingContactForm(form.id || '');
  });

  document.querySelectorAll(rootSelector).forEach(initRoot);
})();
